import { useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import Button from "../../../../shared/components/Button";
import EmptyState from "../../../../shared/components/EmptyState";
import LoadingPage from "../../../../shared/components/LoadingPage";
import PlayerFiltersBar from "../../../../shared/components/PlayerFiltersBar";
import { playerCategoryLabelFromCode } from "../../../../shared/components/PlayerCategorySelect";
import Spinner from "../../../../shared/components/Spinner";
import UserAvatar from "../../../../shared/components/UserAvatar";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { listPublicPlayers } from "../../shared/services/publicPlayerProfileService";
import { PublicPlayerListItem } from "../../shared/types/publicPlayerProfile";
import "./AthletesPage.css";

function athleteCategoryClass(categoryCode?: string | null) {
  const normalized = String(categoryCode || "none").trim().toLowerCase();
  if (normalized === "open") return "open";
  if (/^[2-7]a$/.test(normalized)) return normalized;
  return "none";
}

function athleteCategoryLabel(athlete: PublicPlayerListItem) {
  return (
    athlete.categoryLabel ||
    playerCategoryLabelFromCode(athlete.categoryCode) ||
    siteTexts.common.category.notInformed
  );
}

export default function AthletesPage() {
  const t = siteTexts.athletesPage;
  const [athletes, setAthletes] = useState<PublicPlayerListItem[]>([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sexFilter, setSexFilter] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearchValue(searchDraft);
    }, 1000);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [searchDraft]);

  useEffect(() => {
    let active = true;
    const isInitialRequest = firstLoadRef.current;
    if (isInitialRequest) {
      setListLoading(false);
    } else {
      setListLoading(true);
    }
    setError("");
    listPublicPlayers({
      search: searchValue,
      category: categoryFilter,
      sex: sexFilter,
    })
      .then((response) => {
        if (!active) return;
        setAthletes(response.players || []);
      })
      .catch(() => {
        if (!active) return;
        setAthletes([]);
        setError(t.loadError);
      })
      .finally(() => {
        if (!active) return;
        if (isInitialRequest) {
          firstLoadRef.current = false;
          setHasLoadedOnce(true);
        }
        setListLoading(false);
      });

    return () => {
      active = false;
    };
  }, [categoryFilter, reloadKey, searchValue, sexFilter, t.loadError]);

  const renderedAthletes = useMemo(
    () =>
      athletes.filter((athlete) => {
        const safeId = Number(athlete.id || 0);
        return Number.isFinite(safeId) && safeId > 0 && athlete.displayName;
      }),
    [athletes],
  );

  return (
    <AppLayout
      title={<span className="athletes-page-title">{t.title}</span>}
      subtitle={t.subtitle}
    >
      <main className="athletes-page">
        <section className="athletes-page__filters-panel">
          <PlayerFiltersBar
            ariaLabel={t.title}
            searchValue={searchDraft}
            searchPlaceholder={t.searchPlaceholder}
            searchAriaLabel={t.searchPlaceholder}
            onSearchChange={setSearchDraft}
            onSearchClear={() => setSearchDraft("")}
            categoryLabel={t.categoryFilter}
            categoryValue={categoryFilter}
            onCategoryChange={setCategoryFilter}
            sexLabel={t.sexFilter}
            sexValue={sexFilter}
            onSexChange={setSexFilter}
          />
        </section>

        {!hasLoadedOnce ? (
          <LoadingPage label={siteTexts.common.loading} />
        ) : error ? (
          <section className="athletes-page__error" role="alert">
            <span className="material-symbols-outlined" aria-hidden="true">
              error
            </span>
            <div>
              <strong>{error}</strong>
              <Button
                type="button"
                variant="danger"
                onClick={() => setReloadKey((current) => current + 1)}
              >
                {siteTexts.common.serverUnavailable.actions.retry}
              </Button>
            </div>
          </section>
        ) : renderedAthletes.length === 0 && !listLoading ? (
          <EmptyState message={t.empty} />
        ) : (
          <section className="athletes-page__results" aria-busy={listLoading}>
            {listLoading ? (
              <div
                className="athletes-page__loading-bar"
                role="status"
                aria-live="polite"
              >
                <Spinner size="sm" />
                <span>{siteTexts.common.loading}</span>
              </div>
            ) : null}
            {renderedAthletes.length === 0 ? (
              <div className="athletes-page__loading-empty" />
            ) : (
              <section className="athletes-page__grid" aria-label={t.title}>
                {renderedAthletes.map((athlete) => {
                  const categoryClass = athleteCategoryClass(
                    athlete.categoryCode,
                  );

                  return (
                    <a
                      key={athlete.id}
                      className={`athletes-page__card athletes-page__card--category-${categoryClass}`}
                      href={`#/jogadores/${athlete.id}`}
                    >
                      <span className="athletes-page__avatar-frame">
                        <UserAvatar
                          name={athlete.displayName}
                          avatarUrl={athlete.avatarUrl || ""}
                          sex={athlete.sex || ""}
                          size={82}
                          className="athletes-page__avatar"
                        />
                      </span>
                      <span className="athletes-page__identity">
                        <strong>{athlete.displayName}</strong>
                        <span>{athleteCategoryLabel(athlete)}</span>
                      </span>
                    </a>
                  );
                })}
              </section>
            )}
          </section>
        )}
      </main>
    </AppLayout>
  );
}
